import { useState, useEffect } from 'react';
import { FiClock, FiCoffee, FiCheckCircle, FiChevronRight } from 'react-icons/fi';

const KitchenOrders = () => {
  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Load orders from localStorage on component mount
  useEffect(() => {
    const savedOrders = localStorage.getItem('kitchenOrders');
    try {
      const parsedOrders = savedOrders ? JSON.parse(savedOrders) : [];
      setOrders(parsedOrders);
    } catch (error) {
      console.error('Error parsing orders:', error);
      setOrders([]);
    }
  }, []);

  // Function to update order status
  const updateOrderStatus = (orderId, newStatus) => {
    const updatedOrders = orders.map(order => 
      order.id === orderId ? { ...order, status: newStatus } : order
    );
    setOrders(updatedOrders);
    localStorage.setItem('kitchenOrders', JSON.stringify(updatedOrders));
    setExpandedOrder(null);
  };

  // Function to complete order (remove from ready list)
  const completeOrder = (orderId) => {
    const updatedOrders = orders.filter(order => order.id !== orderId);
    setOrders(updatedOrders);
    localStorage.setItem('kitchenOrders', JSON.stringify(updatedOrders));
  };

  // Toggle order details
  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Filter orders by status
  const pendingOrders = orders.filter(order => order.status === 'pending');
  const preparingOrders = orders.filter(order => order.status === 'preparing');
  const readyOrders = orders.filter(order => order.status === 'ready');

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Kitchen Orders Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pending Orders Column */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-orange-500 p-4 flex items-center">
            <FiClock className="text-white mr-2" size={20} />
            <h2 className="text-lg font-semibold text-white">Pending ({pendingOrders.length})</h2>
          </div>
          <div className="p-4">
            {pendingOrders.length > 0 ? (
              pendingOrders.map(order => (
                <div key={order.id} className="mb-4 last:mb-0 border-b border-gray-100 pb-4 last:border-0">
                  <div 
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleOrderDetails(order.id)}
                  >
                    <div>
                      <h3 className="font-bold text-gray-800">{order.orderNumber}</h3>
                      <p className="text-sm text-gray-600">
                        {order.orderType === 'Dine In' ? `Table ${order.tableNumber}` : `Takeout - ${order.customerName}`}
                      </p>
                    </div>
                    <FiChevronRight 
                      className={`transition-transform ${expandedOrder === order.id ? 'transform rotate-90' : ''}`} 
                    />
                  </div>
                  
                  {expandedOrder === order.id && (
                    <div className="mt-3 pl-2">
                      <div className="mb-3">
                        {order.items.map(item => (
                          <div key={item.cartId} className="mb-2">
                            <p className="font-medium">{item.quantity}x {item.name}</p>
                            {item.specialInstructions && (
                              <p className="text-xs text-gray-500 italic">Note: {item.specialInstructions}</p>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'preparing')}
                        className="w-full py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                      >
                        Start Preparing
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No pending orders</p>
            )}
          </div>
        </div>

        {/* Preparing Orders Column */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-blue-500 p-4 flex items-center">
            <FiCoffee className="text-white mr-2" size={20} />
            <h2 className="text-lg font-semibold text-white">Preparing ({preparingOrders.length})</h2>
          </div>
          <div className="p-4">
            {preparingOrders.length > 0 ? (
              preparingOrders.map(order => (
                <div key={order.id} className="mb-4 last:mb-0 border-b border-gray-100 pb-4 last:border-0">
                  <div 
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleOrderDetails(order.id)}
                  >
                    <div>
                      <h3 className="font-bold text-gray-800">{order.orderNumber}</h3>
                      <p className="text-sm text-gray-600">
                        {order.orderType === 'Dine In' ? `Table ${order.tableNumber}` : `Takeout - ${order.customerName}`}
                      </p>
                    </div>
                    <FiChevronRight 
                      className={`transition-transform ${expandedOrder === order.id ? 'transform rotate-90' : ''}`} 
                    />
                  </div>
                  
                  {expandedOrder === order.id && (
                    <div className="mt-3 pl-2">
                      <div className="mb-3">
                        {order.items.map(item => (
                          <div key={item.cartId} className="mb-2">
                            <p className="font-medium">{item.quantity}x {item.name}</p>
                            {item.specialInstructions && (
                              <p className="text-xs text-gray-500 italic">Note: {item.specialInstructions}</p>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'ready')}
                        className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        Mark as Ready
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No orders being prepared</p>
            )}
          </div>
        </div>

        {/* Ready Orders Column */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-green-500 p-4 flex items-center">
            <FiCheckCircle className="text-white mr-2" size={20} />
            <h2 className="text-lg font-semibold text-white">Ready ({readyOrders.length})</h2>
          </div>
          <div className="p-4">
            {readyOrders.length > 0 ? (
              readyOrders.map(order => (
                <div key={order.id} className="mb-4 last:mb-0 border-b border-gray-100 pb-4 last:border-0">
                  <div 
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleOrderDetails(order.id)}
                  >
                    <div>
                      <h3 className="font-bold text-gray-800">{order.orderNumber}</h3>
                      <p className="text-sm text-gray-600">
                        {order.orderType === 'Dine In' ? `Table ${order.tableNumber}` : `Takeout - ${order.customerName}`}
                      </p>
                    </div>
                    <FiChevronRight 
                      className={`transition-transform ${expandedOrder === order.id ? 'transform rotate-90' : ''}`} 
                    />
                  </div>
                  
                  {expandedOrder === order.id && (
                    <div className="mt-3 pl-2">
                      <div className="mb-3">
                        {order.items.map(item => (
                          <div key={item.cartId} className="mb-2">
                            <p className="font-medium">{item.quantity}x {item.name}</p>
                            {item.specialInstructions && (
                              <p className="text-xs text-gray-500 italic">Note: {item.specialInstructions}</p>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => completeOrder(order.id)}
                        className="w-full py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      >
                        Complete Order
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No orders ready</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KitchenOrders;